import { Injectable } from '@angular/core';

const MAX_ENTRIES = 500;
const LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;

/**
 * Buffers console output + uncaught errors so the support chat widget can
 * attach a "console log" file on demand. The browser has no API to read
 * console history that predates this service, so it only captures from the
 * moment it's first injected (i.e. roughly from login onward, since the
 * widget that injects it mounts as soon as the authenticated shell does) —
 * not the full page lifetime.
 */
@Injectable({ providedIn: 'root' })
export class ConsoleLogCaptureService {
  private entries: string[] = [];

  constructor() {
    this.patchConsole();
    this.patchWindowErrors();
  }

  private patchConsole(): void {
    LEVELS.forEach(level => {
      const original = console[level].bind(console);
      console[level] = (...args: any[]) => {
        this.record(level, args);
        original(...args);
      };
    });
  }

  private patchWindowErrors(): void {
    window.addEventListener('error', (e: ErrorEvent) => {
      this.record('error', [e.message, `${e.filename}:${e.lineno}:${e.colno}`]);
    });
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      this.record('error', ['Unhandled promise rejection:', e.reason]);
    });
  }

  private record(level: string, args: any[]): void {
    const text = args.map(a => this.stringify(a)).join(' ');
    this.entries.push(`[${new Date().toISOString()}] [${level.toUpperCase()}] ${text}`);
    if (this.entries.length > MAX_ENTRIES) this.entries.shift();
  }

  private stringify(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.stack || value.message;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  getLogText(): string {
    return this.entries.length ? this.entries.join('\n') : '(no console output captured yet)';
  }
}
